import React, { Component } from "react";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { get } from "lodash";
import PropTypes from "prop-types";
import classNames from "classnames";
import api from "APP_ROOT/api";
import {
  INVENTORIES,
  FLAG_TYPES,
  TRACE_TYPES,
  MODIFIED,
  SUGGESTED_RETAIL
} from "MODULES/inventories/constants";
import InventoryQuantityCell from "MODULES/shared/components/InventoryQuantityCell";
import InventoryStatusCell from "MODULES/shared/components/InventoryStatusCell";
import InventoryTagDateCell from "MODULES/shared/components/InventoryTagDateCell";
import InventoryListingOverrideCell from "MODULES/shared/components/InventoryListingOverrideCell";
import {
  NoteTooltip as IconTooltip,
  NoteTooltipTableCell
} from "MODULES/shared/components/NoteTooltip";
import { DataTable, LinkedTableRow } from "MODULES/shared/components";
import { miniTable } from "MODULES/shared/decorators";
import withSubscription from "MODULES/Subscription/withSubscription";
import { shortDateYY } from "LIB/formatters/date";
import { getFirstNameLastInitial, truncate } from "LIB/formatters/text";
import { dollars } from "LIB/formatters/currency";
import Controls from "MODULES/inventories/components/Controls";
import ModifiedFlag, {
  columnStyle
} from "MODULES/parts/components/ModifiedFlag";
import LocationFlag from "MODULES/shared/components/LocationFlag/LocationFlag";
import { inventoryTaggedBy } from "MODULES/inventories/helpers";
import AuditedNoteTooltip from "MODULES/shared/components/NoteTooltip/AuditedNoteTooltip";
import { displaySrp } from "../../../helpers";

export class Table extends Component {
  static propTypes = {
    currentPartId: PropTypes.number,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        condition: PropTypes.string.isRequired,
        date_received: PropTypes.string.isRequired,
        suggested_retail: PropTypes.string.isRequired,
        quantity: PropTypes.number.isRequired,
        sellable_status: PropTypes.string.isRequired,
        store_owner_id: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        store_location_id: PropTypes.string.isRequired,
        tag_date: PropTypes.string,
        traced_to: PropTypes.string,
        tagged_by: PropTypes.string,
        repair_order_tagged_by: PropTypes.string
      })
    ).isRequired
  };

  renderInventoryRow = (inventory, i, renderDummyValues) => {
    const activeFlags = Object.keys(FLAG_TYPES).filter(t => inventory[t]);
    const hasModifiedFlag = activeFlags.includes(MODIFIED);
    const onlyModifiedFlag = activeFlags.length === 1 && hasModifiedFlag;

    const flagTypes = activeFlags.map(t => FLAG_TYPES[t].label).join(", ");

    return (
      <LinkedTableRow
        to={`/inventories/${inventory.uuid}`}
        key={renderDummyValues ? `d-${i}` : i}
        className={classNames({ "text-muted": renderDummyValues })}
      >
        <td>{renderDummyValues ? `${i + 1}D` : i + 1}</td>
        <InventoryStatusCell inventory={inventory}>
          <InventoryListingOverrideCell
            inventory={inventory}
            renderDummyValues={renderDummyValues}
          />
        </InventoryStatusCell>
        <td>{!renderDummyValues && shortDateYY(inventory.date_received)}</td>
        <td>
          {!renderDummyValues &&
            inventory.buyer &&
            truncate(getFirstNameLastInitial(inventory.buyer.full_name), 14)}
        </td>
        <td className="text-center">
          {!renderDummyValues && inventory.serial_number}
        </td>
        <td className="text-center">
          {!renderDummyValues && (
            <LocationFlag
              inventory={inventory}
              location={inventory.store_owner_id}
              consignment_type={inventory.consignment_type}
            />
          )}
        </td>
        <td className="text-center">
          {!renderDummyValues && (
            <LocationFlag
              inventory={inventory}
              location={inventory.store_location_id}
              wareHouseId={inventory.ware_house_id}
            />
          )}
        </td>
        <td className="text-center">
          {flagTypes &&
            !onlyModifiedFlag && (
              <IconTooltip
                icon={"fa-flag"}
                placement={"top"}
                note={flagTypes}
                color={"red"}
              />
            )}
        </td>
        <td>{get(TRACE_TYPES, `${inventory.trace_type}.abbrev`)}</td>
        <td>
          {!renderDummyValues &&
            inventory.traced_to &&
            truncate(inventory.traced_to, 20)}
        </td>
        <InventoryTagDateCell
          inventory={inventory}
          renderDummyValues={renderDummyValues}
        />
        <td>{!renderDummyValues && inventoryTaggedBy(inventory)}</td>
        <td style={columnStyle.flagColumn}>
          {hasModifiedFlag && <ModifiedFlag />}
        </td>
        <td style={columnStyle.conditionColumn}>
          {renderDummyValues
            ? get(INVENTORIES, `condition.${inventory.dummy.condition}.label`)
            : get(INVENTORIES, `condition.${inventory.condition}.label`)}
        </td>
        <td className="text-center">{inventory.release_type}</td>
        <InventoryQuantityCell
          inventory={inventory}
          renderDummyValues={renderDummyValues}
          alignment="center"
        />
        <td className="text-right">
          {!renderDummyValues &&
            inventory.cost_per_unit &&
            dollars(inventory.cost_per_unit)}
        </td>
        <td className="text-center">
          <div style={{ display: "flex", alignItems: "center" }}>
            <div>{displaySrp(renderDummyValues, inventory)}</div>
            <div>
              <AuditedNoteTooltip field={SUGGESTED_RETAIL} obj={inventory} />
            </div>
          </div>
        </td>
        <NoteTooltipTableCell
          note={
            renderDummyValues ? inventory.dummy.note : inventory.important_note
          }
          color={inventory.important_note && "#f44336"}
        />
        <td>{!renderDummyValues && <Controls inventory={inventory} />}</td>
      </LinkedTableRow>
    );
  };

  renderRow = (inventory, i) => {
    if (inventory.dummy) {
      return [
        this.renderInventoryRow(inventory, i),
        this.renderInventoryRow(inventory, i, true)
      ];
    }
    return this.renderInventoryRow(inventory, i);
  };

  render() {
    const { currentPartId, results } = this.props;
    const partMismatch =
      currentPartId &&
      results.some(i => i.part_id.toString() !== currentPartId.toString());

    // NOTE: This guard was put in place after Aero-Zone reported multiple occurrences of this table
    // showing records for the incorrect part.
    if (partMismatch) {
      return (
        <div style={{ color: "red" }}>
          Error loading inventory, please reload page.
        </div>
      );
    }

    return (
      <DataTable.Mini loading={false} numbered>
        <DataTable.Head>
          <th />
          <th />
          <th>Date Rec</th>
          <th>Buyer</th>
          <th className="text-center">Serial #</th>
          <th className="text-center">Store Owner</th>
          <th className="text-center">Store Loc</th>
          <th className="text-center">Flag</th>
          <th>Trace</th>
          <th>Trace To</th>
          <th className="text-center">Tag Date</th>
          <th>Tagged By</th>
          <th />
          <th>Condition</th>
          <th className="text-center">Release</th>
          <th className="text-center">QTY</th>
          <th className="text-right">Cost/Unit</th>
          <th className="text-center">SRP</th>
          <th />
          <th className="text-center" />
        </DataTable.Head>
        <DataTable.Body>
          {results.slice(0, 5).map(this.renderRow)}
        </DataTable.Body>
      </DataTable.Mini>
    );
  }
}

const withResults = miniTable(({ match }) =>
  api.parts.inventories.search(match.params.id)
);

export default connect(state => ({
  currentPartId: state.partDetail.getIn(["part", "id"])
}))(withRouter(withResults(withSubscription("Inventory")(Table))));



// WEBPACK FOOTER //
// ./app/modules/parts/components/Detail/Inventory/Table.jsx